import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useAppOrientation } from '@/lostHooks/orientation/useOrientation';
import { useStore } from '@/store/useStore';

const getGapBetweenChildren = (
  first: HTMLElement,
  second: HTMLElement,
): number => {
  const firstRect = first.getBoundingClientRect();
  const secondRect = second.getBoundingClientRect();

  if (secondRect.left && firstRect.right) {
    return Math.max(0, secondRect.left - firstRect.right);
  }

  const offsetGap = second.offsetLeft - first.offsetLeft - first.offsetWidth;
  return Math.max(0, offsetGap);
};

export const useDraggableContainer = (
  className?: string,
  children?: ReactNode,
) => {
  const wrapperType = useStore((s) => s.runtime.wrapperType);

  const orientation = useAppOrientation();

  const isPortraitMobile = wrapperType.isAndroid && orientation === 'portrait';
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollStep, setScrollStep] = useState(0);

  const calculateScrollStep = useCallback(() => {
    const content = contentRef.current;
    if (!content) return 0;

    const firstChild = content.children.item(0) as HTMLElement | null;
    const secondChild = content.children.item(1) as HTMLElement | null;
    if (!firstChild) return 0;

    const firstRect = firstChild.getBoundingClientRect();
    const firstWidth = firstRect.width || firstChild.offsetWidth || 0;
    if (!firstWidth) return 0;

    const gap = secondChild
      ? getGapBetweenChildren(firstChild, secondChild)
      : 0;
    return firstWidth + gap / 2;
  }, []);

  const updateScrollState = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const { scrollWidth, clientWidth, scrollLeft } = scrollElement;
    const hasScrollableOverflow = scrollWidth > clientWidth + 1;
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

    setHasOverflow(hasScrollableOverflow);
    setCanScrollLeft(hasScrollableOverflow && scrollLeft > 0);
    setCanScrollRight(hasScrollableOverflow && scrollLeft < maxScrollLeft - 1);
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setScrollStep(calculateScrollStep());
  }, [children, orientation, updateScrollState, calculateScrollStep]);

  const onScroll = useCallback(() => {
    updateScrollState();
    setScrollStep(calculateScrollStep());
  }, [updateScrollState, calculateScrollStep]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    scrollElement.addEventListener('scroll', onScroll);
    return () => scrollElement.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const scrollElement = scrollRef.current;
    const contentElement = contentRef.current;
    if (!scrollElement || !contentElement) return undefined;

    const observer = new ResizeObserver(() => {
      updateScrollState();
      setScrollStep(calculateScrollStep());
    });
    observer.observe(scrollElement);
    observer.observe(contentElement);

    return () => observer.disconnect();
  }, [updateScrollState, calculateScrollStep]);

  const handleScroll = useCallback(
    (direction: 'left' | 'right') => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) return;

      const step = scrollStep || scrollElement.clientWidth / 2;
      const left = direction === 'left' ? -step : step;
      scrollElement.scrollBy({ left, behavior: 'smooth' });
    },
    [scrollStep],
  );

  const containerClassName = useMemo(
    () =>
      [
        'draggable-container',
        isPortraitMobile ? 'draggable-container--portrait' : '',
        className || '',
      ]
        .filter(Boolean)
        .join(' '),
    [className, isPortraitMobile],
  );

  const canShowButtons = !wrapperType.isMobile;
  const showLeftButton = canShowButtons && hasOverflow && canScrollLeft;
  const showRightButton = canShowButtons && hasOverflow && canScrollRight;

  return {
    containerClassName,
    showLeftButton,
    showRightButton,
    onScrollLeft: () => handleScroll('left'),
    onScrollRight: () => handleScroll('right'),
    onScroll,
    scrollRef,
    contentRef,
  };
};
